import React, { useState } from 'react';

interface LogEntry {
  timestamp: Date;
  level: 'log' | 'warn' | 'error';
  message: string;
  details?: any;
}

export function RealTimeMonitor() {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [realTimeLogs, setRealTimeLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<'all' | 'error' | 'warn' | 'log'>('all');

  const startMonitoring = () => {
    setIsMonitoring(true);
    setRealTimeLogs([]);

    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
    };

    console.log = (...args) => {
      originalConsole.log(...args);
      addLogEntry('log', args.join(' '), args);
    };

    console.warn = (...args) => {
      originalConsole.warn(...args);
      addLogEntry('warn', args.join(' '), args);
    };

    console.error = (...args) => {
      originalConsole.error(...args);
      addLogEntry('error', args.join(' '), args);
    };

    setTimeout(() => {
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      setIsMonitoring(false);
    }, 30000);
  };

  const addLogEntry = (level: LogEntry['level'], message: string, details?: any) => {
    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      message,
      details,
    };

    setRealTimeLogs((prev) => [...prev.slice(-49), entry]);
  };

  const stopMonitoring = () => {
    setIsMonitoring(false);
  };

  const clearLogs = () => {
    setRealTimeLogs([]);
  };

  const filteredLogs = realTimeLogs.filter((log) => filter === 'all' || log.level === filter);

  const getLevelIcon = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return '🚨';
      case 'warn':
        return '⚠️';
      case 'log':
        return 'ℹ️';
      default:
        return '📝';
    }
  };

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return 'text-orange-50 dark:text-orange-30';
      case 'warn':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'log':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-neutral-600 dark:text-neutral-400';
    }
  };

  return (
    <div className="mb-6 border border-neutral-20 dark:border-neutral-70 rounded-lg bg-white dark:bg-neutral-90">
      <div className="p-4 border-b border-neutral-20 dark:border-neutral-70">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-neutral-90 dark:text-white">📡 Real-time Monitor</h3>
          <div className="flex gap-2">
            {isMonitoring ? (
              <button
                onClick={stopMonitoring}
                className="px-3 py-1 bg-orange-50 hover:bg-orange-60 text-white rounded text-sm transition-colors"
              >
                ⏹️ Stop
              </button>
            ) : (
              <button
                onClick={startMonitoring}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
              >
                ▶️ Monitor
              </button>
            )}
            <button
              onClick={clearLogs}
              className="px-3 py-1 bg-neutral-600 hover:bg-neutral-700 text-white rounded text-sm transition-colors"
            >
              🗑️ Clear
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <div
            className={`w-2 h-2 rounded-full ${isMonitoring ? 'bg-green-500 animate-pulse' : 'bg-neutral-400'}`}
          ></div>
          <span className="text-neutral-70 dark:text-neutral-30">
            {isMonitoring ? 'Monitoring active (30s)' : 'Monitoring inactive'}
          </span>
          {realTimeLogs.length > 0 && (
            <span className="text-neutral-60 dark:text-neutral-40">• {realTimeLogs.length} entries</span>
          )}
        </div>

        <div className="flex gap-2 mt-3">
          {(['all', 'error', 'warn', 'log'] as const).map((level) => (
            <button
              key={level}
              onClick={() => setFilter(level)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                filter === level
                  ? 'bg-blue-600 text-white'
                  : 'bg-neutral-100 dark:bg-neutral-80 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-70'
              }`}
            >
              {level.charAt(0).toUpperCase() + level.slice(1)}
              {level !== 'all' && (
                <span className="ml-1 opacity-75">({realTimeLogs.filter((log) => log.level === level).length})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto">
        {filteredLogs.length === 0 ? (
          <div className="p-4 text-center text-neutral-500 dark:text-neutral-400 text-sm">
            {isMonitoring ? 'No logs yet...' : 'Start monitoring to see real-time logs'}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredLogs.map((log, index) => (
              <div
                key={index}
                className="p-2 text-xs font-mono border-l-2 border-neutral-200 dark:border-neutral-700 hover:bg-neutral-5 dark:hover:bg-neutral-85 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span>{getLevelIcon(log.level)}</span>
                  <span className={getLevelColor(log.level)}>{log.level.toUpperCase()}</span>
                  <span className="text-neutral-500 dark:text-neutral-400">{log.timestamp.toLocaleTimeString()}</span>
                </div>
                <div className="text-neutral-90 dark:text-white pl-6">{log.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
